import { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowUp, Mic, X } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import type { V2Turn, V2Section } from '../api/types';
import { api } from '../api/client';
import { useSessionV2 } from '../hooks/useSessionV2';
import './QueueChat.css';

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

function getUrlParam(key: string): string | null {
  return new URLSearchParams(window.location.search).get(key);
}

function stripContextPrefixes(text: string): string {
  // Strip all known context prefixes — may appear multiple times if API also prepends
  let clean = text;
  // [Context: ...] prefix (our format)
  clean = clean.replace(/^\[Context:\s*[^\]]*\]\s*/gi, '');
  // [post_id: ...] or [post_id: 587, title: ...] (API format)
  clean = clean.replace(/^\[post_id:\s*[^\]]*\]\s*/gi, '');
  // [Queue Message #N from ...]: prefix
  clean = clean.replace(/^\[Queue Message[^\]]*\]:\s*/gi, '');
  // [IACP:...] prefix
  clean = clean.replace(/^\[IACP:[^\]]*\]\s*/gi, '');
  return clean.trim();
}

const mdComponents = {
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
};

function SectionContent({ section }: { section: V2Section }) {
  if (section.kind === 'text') {
    return (
      <div className="qc-text">
        <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
          {section.content || ''}
        </Markdown>
      </div>
    );
  }
  if (section.kind === 'tool') {
    return <div className="qc-tool">{section.label || 'tool'}</div>;
  }
  return null;
}

export function QueueChat({ sessionName }: { sessionName: string }) {
  const context = getUrlParam('context');
  const title = getUrlParam('title') || sessionName;
  const { data } = useSessionV2(sessionName, 3000);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const mediaRecRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const sendingRef = useRef(false);

  // Auto-scroll
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [data]);

  const buildMessage = useCallback((text: string): string => {
    if (context) {
      return `[Context: ${context}] ${text}`;
    }
    return text;
  }, [context]);

  const send = useCallback(() => {
    if (sendingRef.current) return;
    const text = input.trim();
    if (!text) return;
    sendingRef.current = true;
    setInput('');
    setSending(true);
    api.sessions.sendInput(sessionName, buildMessage(text)).finally(() => {
      sendingRef.current = false;
    });
  }, [input, sessionName, buildMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Clear sending when new turn appears
  const turnCountRef = useRef(data?.turn_count || 0);
  useEffect(() => {
    const newCount = data?.turn_count || 0;
    if (sending && newCount > turnCountRef.current) {
      setSending(false);
    }
    turnCountRef.current = newCount;
  }, [data?.turn_count, sending]);

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType });
      mediaRecRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 500) return;
        setSending(true);
        try {
          // Via api.transcribe: bearer token + 401-retry (#534-Gate).
          const ext = mimeType.startsWith('audio/mp4') ? 'm4a' : 'webm';
          const d = await api.transcribe(blob, `recording.${ext}`);
          if (d.text) api.sessions.sendInput(sessionName, buildMessage(d.text));
        } catch { setSending(false); }
      };
      rec.start(1000);
      setRecording(true);
    } catch { /* mic error */ }
  };

  const stopRecording = () => {
    setRecording(false);
    const rec = mediaRecRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  };

  const cancelRecording = () => {
    setRecording(false);
    const rec = mediaRecRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.ondataavailable = null;
      rec.onstop = () => { audioStreamRef.current?.getTracks().forEach((t) => t.stop()); };
      rec.stop();
    }
    chunksRef.current = [];
  };

  return (
    <div className="queue-chat">
      <div className="qc-header">
        <div className="qc-title">{title}</div>
      </div>

      <div className="qc-stream" ref={streamRef}>
        {data?.turns.map((turn) => (
          <div key={turn.id} className="qc-turn">
            {turn.user && (
              <div className="qc-user">
                <div className="qc-user-marker" />
                <div className="qc-user-text">{stripContextPrefixes(turn.user)}</div>
              </div>
            )}
            <div className="qc-agent">
              {turn.sections.filter((s) => s.kind === 'text' || s.kind === 'tool').map((sec, i) => (
                <SectionContent key={i} section={sec} />
              ))}
            </div>
          </div>
        ))}
        {sending && (
          <div className="qc-sending">
            <span /><span /><span />
          </div>
        )}
      </div>

      <div className="qc-input-bar">
        {recording ? (
          <>
            <button className="qc-btn qc-cancel" onClick={cancelRecording}><X size={16} /></button>
            <div className="qc-recording-label">Recording...</div>
            <button className="qc-btn qc-send" onClick={stopRecording}><ArrowUp size={16} /></button>
          </>
        ) : (
          <>
            <button className="qc-btn" onClick={startRecording}><Mic size={16} /></button>
            <textarea
              className="qc-input"
              placeholder="Message..."
              value={input}
              rows={1}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
              }}
              onKeyDown={handleKeyDown}
            />
            <button className="qc-btn qc-send" onClick={send}><ArrowUp size={16} /></button>
          </>
        )}
      </div>
    </div>
  );
}
